import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { Calendar, Users, Trophy, DollarSign, ChevronRight } from "lucide-react";

const LandingPage = () => {
  const { isAuthenticated, isAdmin } = useAuth();

  return (
    <div className="min-h-screen bg-background relative">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="font-heading font-bold text-primary-foreground text-lg">TTP</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-text-primary">TTP Play Day</h1>
              <p className="text-xs text-text-secondary">Rodeo Management System</p>
            </div>
          </div>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to={isAdmin ? "/admin" : "/contestant"}>
                <Button className="bg-primary text-primary-foreground hover:bg-primary-hover font-semibold" data-testid="dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-text-primary hover:bg-secondary" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="register-btn">
                    Register
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1578796232543-5e74775b6e86?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-2xl">
            <h2 className="font-heading font-black text-5xl sm:text-6xl text-text-primary leading-tight mb-6">
              Rodeo Play Days<br />
              <span className="text-primary">Made Simple</span>
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Complete event management for barrel racing, pole bending, goat tying, breakaway, 
              team roping, and more. Register contestants, manage draws, track times, 
              and calculate payouts all in one place.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90 font-bold text-lg px-8" data-testid="hero-register-btn">
                  Get Started <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-2 border-primary text-primary-foreground hover:bg-primary/10 font-bold text-lg px-8" data-testid="hero-login-btn">
                  Admin Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-heading font-bold text-3xl text-text-primary text-center mb-12">
            Everything You Need
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={<Users className="h-8 w-8" />}
              title="Contestant Database"
              description="Manage all contestants, track memberships, and maintain complete records."
            />
            <FeatureCard
              icon={<Calendar className="h-8 w-8" />}
              title="Play Day Setup"
              description="Create play days, configure events, and set custom entry fees."
            />
            <FeatureCard
              icon={<Trophy className="h-8 w-8" />}
              title="Random Draws"
              description="Generate fair random draw orders and input times with auto-calculated totals."
            />
            <FeatureCard
              icon={<DollarSign className="h-8 w-8" />}
              title="Payouts & Points"
              description="Automatic payout calculations and points tracking for year-end standings."
            />
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-heading font-bold text-3xl text-text-primary text-center mb-4">
            22 Events Supported
          </h3>
          <p className="text-text-secondary text-center mb-12 max-w-2xl mx-auto">
            From Mini to Adult age groups, we support all standard play day events
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {["Barrels", "Poles", "Flags", "Goats", "Breakaway", "Ribbon Roping", "Team Roping"].map((event) => (
              <span 
                key={event}
                className="px-4 py-2 bg-white rounded-full text-text-primary font-medium border border-primary/30 shadow-sm"
              >
                {event}
              </span>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-8">
            {["Mini", "Junior", "Senior", "Adult"].map((age) => (
              <span 
                key={age}
                className="px-6 py-2 bg-primary/20 rounded-lg text-primary-foreground font-heading font-bold"
              >
                {age}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-heading font-bold text-3xl text-primary-foreground mb-4">
            Ready to Simplify Your Play Days?
          </h3>
          <p className="text-primary-foreground/80 mb-8">
            Join TTP Play Day and make your rodeo events run smoother than ever.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-primary-foreground hover:bg-white/90 font-bold text-lg px-8" data-testid="cta-register-btn">
              Register Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-text-primary text-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} TTP Play Day. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-surface p-6 rounded-xl border border-primary/20 card-hover" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center text-primary mb-4">
      {icon}
    </div>
    <h4 className="font-heading font-bold text-lg text-text-primary mb-2">{title}</h4>
    <p className="text-text-secondary text-sm">{description}</p>
  </div>
);

export default LandingPage;
